import React, { FC, useMemo, useState } from "react";
import { SORT_DIRECTION, TABLE_HEADER_ARRAY } from "../../utils/Types";
import {
  Box,
  Table,
  TableContainer,
  TableContainerProps,
  TableProps,
} from "@mui/material";
import DynamicTableBody from "./DynamicTableBody";
import DynamicTableHead from "./DynamicTableHead";
import { listDynamicSort } from "../../helpers/listDynamicSort";
import DynamicTablePagination from "./DynamicTablePagination";
import { globalStyles } from "../../styles/GlobalStyles";
import EmptyComponent from "../EmptyComponent";
import TableSkeletonLoader from "../skeletonLoaders/TableSkeletonLoader";

export type SORT_DETAILS = {
  sortKey?: string;
  sortDirection?: SORT_DIRECTION;
};

export type DynamicTableProps = {
  headers: TABLE_HEADER_ARRAY;
  rowData: Array<any>;
  tableContainerProps?: TableContainerProps;
  tableProps?: TableProps;
  stickyHeader?: boolean;
  tableHeight?: number;
  onRowClick?: (tableItem: any) => void;
  enablePagination?: boolean;
  totalPage?: number;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  isLoading?: boolean;
  disableSorting?: boolean;
  transparentRows?: boolean;
  enableRowSelection?: boolean;
  onSelectionChange?: (rows: any[]) => void;
  renderHeaderMenu?: () => React.ReactNode;
  onCheck?: (tableItem: any) => void;
  enableRowExpansion?: boolean;
  getRowId?: (row: any) => string | number;
  isRowExpandable?: (row: any) => boolean;
  renderExpandedRow?: (row: any) => React.ReactNode;
  initialSortDetails?: SORT_DETAILS;
  emptyStateTitle?: string;
};

const DynamicTable: FC<DynamicTableProps> = ({
  headers,
  rowData,
  tableContainerProps,
  tableProps,
  stickyHeader = true,
  tableHeight = "64vh",
  onRowClick,
  enablePagination = false,
  totalPage = 1,
  onPageChange,
  currentPage = 1,
  isLoading,
  disableSorting = false,
  transparentRows = false,
  enableRowSelection = false,
  onSelectionChange,
  renderHeaderMenu,
  onCheck,
  enableRowExpansion = false,
  getRowId,
  isRowExpandable,
  renderExpandedRow,
  initialSortDetails,
  emptyStateTitle = "No_data_to_display",
}) => {
  const [sortDetails, setSortDetails] = useState<SORT_DETAILS>(
    initialSortDetails ?? {
      sortKey: "caseId",
      sortDirection: SORT_DIRECTION.ASCENDING,
    }
  );

  const [hasUserSorted, setHasUserSorted] = useState(!!initialSortDetails);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());

  const sortedRowData = useMemo(() => {
    if (disableSorting || !hasUserSorted) {
      return rowData;
    }
    const sorted = listDynamicSort(rowData, sortDetails);
    const regularRows = sorted.filter((row: any) => !row?._isTotalRow);
    const totalRows = sorted.filter((row: any) => row?._isTotalRow);
    return [...regularRows, ...totalRows];
  }, [rowData, sortDetails, disableSorting, hasUserSorted]);

  const handleSortChange = (details: SORT_DETAILS) => {
    setHasUserSorted(true);
    setSortDetails(details);
  };

  const isEmpty = useMemo(() => !!rowData && rowData.length === 0, [rowData]);

  const handleRowCheck = (row: any) => {
    if (!enableRowSelection) return;

    const exists = selectedRows.some((r) => r.caseId === row.caseId);
    const updated = exists
      ? selectedRows.filter((r) => r.caseId !== row.caseId)
      : [...selectedRows, row];

    setSelectedRows(updated);
    onSelectionChange?.(updated);
  };

  if (isLoading) {
    return (
      <TableSkeletonLoader
        columns={headers.length}
        tableHeight={stickyHeader ? tableHeight : undefined}
      />
    );
  }
  

  const resolvedGetRowId = (row: any) => {
    if (getRowId) return getRowId(row);
    // fallback (keeps old behavior untouched)
    return row?.id ?? row?.caseId;
  };

  const toggleExpand = (row: any) => {
    const id = resolvedGetRowId(row);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isEmpty) {
    return (
      <>
        <Box sx={globalStyles.colCenter}>
          <EmptyComponent title={emptyStateTitle} />
        </Box>

        {enablePagination && (
          <DynamicTablePagination
            count={totalPage}
            onChange={onPageChange}
            currentPage={currentPage}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Box flex={1}>
        <TableContainer
          sx={{
            boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.07)",
            borderRadius: 2,
            maxHeight: stickyHeader ? tableHeight : undefined,
            "& .MuiTableContainer-root": { overflow: "auto" },
            ...tableContainerProps?.sx,
          }}
          {...tableContainerProps}
        >
          <Table {...tableProps} stickyHeader={stickyHeader}>
            <DynamicTableHead
              data={headers}
              enableRowSelection={enableRowSelection}
              renderHeaderMenu={renderHeaderMenu}
              {...(!disableSorting && {
                sortDetails,
                setSortDetails: handleSortChange,
              })}
            />

            <DynamicTableBody
              data={sortedRowData}
              headersArr={headers}
              onRowClick={onRowClick}
              transparentRows={transparentRows}
              enableRowSelection={enableRowSelection}
              selectedRows={selectedRows}
              onCheck={enableRowSelection ? handleRowCheck : onCheck}
              enableRowExpansion={enableRowExpansion}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
              getRowId={resolvedGetRowId}
              isRowExpandable={isRowExpandable}
              renderExpandedRow={renderExpandedRow}
            />
          </Table>
        </TableContainer>
      </Box>

      {enablePagination && (
        <DynamicTablePagination
          count={totalPage}
          onChange={onPageChange}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default DynamicTable;
